import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InsightsIcon from '@mui/icons-material/Insights';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { selectProfile } from 'src/modules/profile/slices';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import {
  CampaignAnalyticsModal,
  CampaignAnalyticsData,
} from 'src/modules/campaigns/components/CampaignAnalyticsModal';
import SaveAsPresetDialog from 'src/modules/campaignPresets/components/SaveAsPresetDialog/SaveAsPresetDialog';
import { canManagePresets } from 'src/modules/campaignPresets/helpers/canManagePresets';
import { canSaveTypeAsPreset } from 'src/modules/campaignPresets/helpers/canSaveTypeAsPreset';
import { campaignBuilderPath } from 'src/modules/campaigns/helpers';
import { Campaign, CampaignConfigSchema } from '../../types';
import { useCampaignFormContext } from '../../context';

interface Props {
  config: CampaignConfigSchema;
  configId: string | null;
  campaign: Campaign | null;
  /** A saved campaign being viewed/edited (not a brand-new or duplicated one). */
  isExistingCampaign: boolean;
}

/**
 * Overflow menu next to the builder title: view analytics, copy as a new campaign,
 * and save the current form state as a reusable custom template.
 */
export const HeaderActions: React.FC<Props> = ({ config, configId, campaign, isExistingCampaign }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = useSelector(getCurrentCompanyId) as string;
  const { profile } = useSelector(selectProfile);
  const { values } = useCampaignFormContext();
  const { getCampaignAnalytics } = useCampaignsApi();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [showSaveAsPreset, setShowSaveAsPreset] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analytics, setAnalytics] = useState<CampaignAnalyticsData | undefined>(
    (location.state as { campaignAnalytics?: CampaignAnalyticsData } | null)?.campaignAnalytics,
  );

  // Creating presets is restricted to managers and above (or admins).
  const userCanManagePresets = canManagePresets(profile);
  const canSaveAsPreset = userCanManagePresets && !!config.type && canSaveTypeAsPreset(config.type);
  const { name } = values;

  // Keyed off the ROLE, not off whether any item will actually render — so a
  // manager creating a campaign of a type that cannot be saved as a preset still
  // gets the ⋮, and it opens an empty menu.
  if (!isExistingCampaign && !analytics && !userCanManagePresets) return null;

  // Analytics are loaded lazily — only when the user opens "View analytics" —
  // so opening the builder does not pay for a request nobody looked at.
  const handleOpenAnalytics = async () => {
    setAnchor(null);
    setShowAnalytics(true);
    if (analytics) return;
    if (!campaign?.id || !companyId) return;

    setAnalyticsLoading(true);
    try {
      const { data } = await getCampaignAnalytics(companyId, campaign.id);
      setAnalytics({
        id: data.campaignId,
        name: campaign.name ?? '',
        scheduledMessages: campaign.scheduledMessages,
        messagesDelivered: data.messagesDelivered,
        messagesPending: data.messagesPending,
        messagesFailed: data.messagesFailed,
        numberOfContacts: data.numberOfContacts,
        totalReplies: data.totalReplies,
        numberOfRespondedContacts: data.numberOfRespondedContacts,
        lastMessageSentAt: data.lastMessageSentAt,
        lastResponseAt: data.lastResponseAt,
      });
    } catch (error) {
      console.error('Failed to load campaign analytics', error);
      // If router state supplied metrics, keep showing them even when a refresh
      // fails. This branch only has no cache because cached data returns above.
      if (!analytics) {
        toast.error('Failed to load campaign analytics. Please try again.');
        setShowAnalytics(false);
      }
    } finally {
      setAnalyticsLoading(false);
    }
  };

  return (
    <>
      <IconButton aria-label="Campaign actions" onClick={e => setAnchor(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Analytics only exist for saved campaigns, never for new ones. */}
        {isExistingCampaign && (
          <MenuItem onClick={handleOpenAnalytics}>
            <ListItemIcon><InsightsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>View analytics</ListItemText>
          </MenuItem>
        )}
        {/* Seed a brand-new campaign from this existing one (table copy action). */}
        {isExistingCampaign && campaign && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              // Share the one switch every other entry point uses, so a rollback
              // of CAMPAIGN_BUILDER_BASE_PATH takes this action with it instead of
              // stranding the user in whichever builder is being rolled back.
              navigate(campaignBuilderPath(`/${campaign.configId || campaign.type}`), {
                state: { duplicateCampaignId: campaign.id },
              });
            }}
          >
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy as a new campaign</ListItemText>
          </MenuItem>
        )}
        {canSaveAsPreset && (
          <MenuItem
            disabled={!name}
            onClick={() => {
              setAnchor(null);
              setShowSaveAsPreset(true);
            }}
          >
            <ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Save as custom template</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {companyId && (
        <SaveAsPresetDialog
          open={showSaveAsPreset}
          companyId={companyId}
          campaignType={config.type}
          sourceConfigId={campaign?.configId ?? configId}
          formValues={values as unknown as Record<string, unknown>}
          onClose={() => setShowSaveAsPreset(false)}
          onSaved={() => setShowSaveAsPreset(false)}
        />
      )}
      <CampaignAnalyticsModal
        open={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        loading={analyticsLoading}
        campaign={
          analytics
            ? {
                ...analytics,
                name: analytics.name || campaign?.name || '',
                scheduledMessages: analytics.scheduledMessages ?? campaign?.scheduledMessages,
              }
            : null
        }
      />
    </>
  );
};
